/**
 * La grille tarifaire des carrières : articles en lignes, carrières en colonnes.
 *
 * *Demandé par le métier le 18/08/2026 : « concernant la grille de tarif il faut pouvoir
 * ajouter des articles, les supprimer, les modifier, modifier les prix. Même si les champs du
 * fichier Excel ne sont pas remplis il faut considérer que l'on pourra renseigner les prix.
 * […] Les tarifs ont une période de validité que je dois pouvoir paramétrer et il faut
 * historiser les tarifs par période. »*
 *
 * ## Un tableau croisé, et pas une liste de prix
 *
 * C'est la forme du classeur que le métier tient déjà, et ce n'est pas qu'une habitude : ce
 * qu'on vient faire ici est comparer le même matériau d'une carrière à l'autre. Une liste de
 * croisements — un article, une carrière, un prix par ligne — donnerait 222 lignes où le
 * tableau en donne 37, et rendrait cette comparaison impossible à l'œil.
 *
 * **Les cases vides sont l'état normal.** Mesuré sur le classeur d'origine : 70 prix pour 222
 * croisements possibles. La grille montre donc combien de cases sont remplies, plutôt que de
 * laisser croire qu'un tableau clairsemé est un tableau incomplet.
 *
 * ## Ce qui se passe quand un prix change ici : rien, ailleurs
 *
 * Aucune contre-étude déjà chiffrée ne bouge. Le prix est *copié* sur la ligne au moment où le
 * conducteur désigne la carrière ; corriger la grille ensuite ne le rattrape pas. C'est la
 * seule forme acceptable — un chiffrage soumis à validation ne doit pas changer sous les pieds
 * de celui qui l'a soumis — et l'écran le dit, pour qu'on ne l'attende pas.
 */
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import {
  adresseClasseurGrille,
  chargerCarrieres,
  chargerGrille,
  creerArticleTarif,
  creerPeriodeTarif,
  dupliquerPeriodeTarif,
  fixerPrixTarif,
  importerGrille,
  modifierArticleTarif,
  modifierPeriodeTarif,
  supprimerArticleTarif,
  supprimerPeriodeTarif,
} from "./api";
import type { ArticleTarif, BilanGrille, Carriere, Grille } from "./api";

type Etat =
  | { phase: "chargement" }
  | { phase: "chargee"; grille: Grille; carrieres: readonly Carriere[] }
  | { phase: "erreur"; raison: string };

/**
 * `6.5300` s'affiche `6,53`.
 *
 * La colonne garde quatre décimales et les rend telles quelles ; les montrer toutes dans un
 * champ de saisie donnerait à relire un prix que personne n'a tapé. Le nettoyage se fait sur
 * la CHAÎNE, sans passer par un nombre — la règle du produit sur l'argent.
 */
export function prixLisible(prix: string): string {
  const net = prix.includes(".") ? prix.replace(/0+$/, "").replace(/\.$/, "") : prix;
  return net.replace(".", ",");
}

/**
 * Les carrières qui font une colonne : les actives, **plus celles qui portent un prix**.
 *
 * Une carrière retirée du référentiel doit garder sa colonne tant qu'un prix la cite, sinon
 * une colonne de l'historique disparaît de l'écran alors que des contre-études s'y réfèrent —
 * et personne ne saurait plus d'où venait le prix qu'elles portent.
 */
export function colonnesDeLaGrille(
  carrieres: readonly Carriere[],
  prix: readonly { readonly carriereId: string }[],
): readonly Carriere[] {
  const citees = new Set(prix.map((p) => p.carriereId));
  return carrieres.filter((c) => c.actif || citees.has(c.id));
}

/** Un champ qui n'envoie sa valeur qu'une fois la saisie terminée. */
function Champ({
  valeur,
  placeholder,
  aligne,
  enAttente,
  ariaLabel,
  infobulle,
  onValider,
}: {
  valeur: string;
  placeholder?: string;
  aligne?: "droite";
  enAttente: boolean;
  ariaLabel?: string;
  /** Ce que le survol montre quand la colonne coupe — sur le champ, pas sur sa cellule. */
  infobulle?: string;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);

  return (
    <input
      className={`saisie${aligne === "droite" ? "" : " saisie--texte"}${enAttente ? " saisie--attente" : ""}`}
      title={infobulle}
      value={brouillon ?? valeur}
      placeholder={placeholder}
      disabled={enAttente}
      aria-label={ariaLabel}
      onChange={(e) => {
        setBrouillon(e.target.value);
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      onBlur={() => {
        const saisi = brouillon;
        setBrouillon(null);
        if (saisi !== null && saisi.trim() !== valeur) onValider(saisi.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setBrouillon(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/**
 * Le panneau des périodes : celle qu'on regarde, ses bornes, et la suivante.
 *
 * Les bornes proposées viennent du **serveur** et enchaînent sur la dernière période : la
 * contrainte de non-chevauchement refuserait autre chose, et laisser l'administrateur
 * retrouver lui-même la bonne date serait lui faire deviner une règle de la base.
 */
function Periodes({
  grille,
  enAttente,
  onChoisir,
  onAgir,
}: {
  grille: Grille;
  enAttente: boolean;
  onChoisir: (id: string) => void;
  onAgir: (action: Promise<unknown>) => void;
}): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const [libelle, setLibelle] = useState(grille.periodeProposee.libelle);
  const [debut, setDebut] = useState(grille.periodeProposee.debut);
  const [fin, setFin] = useState(grille.periodeProposee.fin);
  const [depuis, setDepuis] = useState<string>("");

  const courante = grille.periode;
  const complet = libelle.trim() !== "" && debut !== "" && fin !== "";

  return (
    <div className="periodes">
      <p className="etat">
        <label>
          Période de validité{" "}
          <select
            value={courante?.id ?? ""}
            disabled={grille.periodes.length === 0}
            onChange={(e) => {
              onChoisir(e.target.value);
            }}
          >
            {grille.periodes.length === 0 && <option value="">aucune période</option>}
            {grille.periodes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.libelle} ({p.debut} → {p.fin})
              </option>
            ))}
          </select>
        </label>
        {" · "}
        <button
          type="button"
          className="lien"
          onClick={() => {
            setOuvert(!ouvert);
          }}
        >
          {ouvert ? "fermer" : "gérer les périodes"}
        </button>
      </p>

      {ouvert && (
        <div className="periodes__panneau">
          {courante !== null && (
            <form
              className="ajout"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <span className="periodes__titre">Corriger « {courante.libelle} »</span>
              <Champ
                valeur={courante.libelle}
                enAttente={enAttente}
                ariaLabel="Libellé de la période"
                onValider={(valeur) => {
                  onAgir(modifierPeriodeTarif(courante.id, { libelle: valeur }));
                }}
              />
              <BorneDate
                valeur={courante.debut}
                etiquette="Début"
                enAttente={enAttente}
                onValider={(valeur) => {
                  onAgir(modifierPeriodeTarif(courante.id, { debut: valeur }));
                }}
              />
              <BorneDate
                valeur={courante.fin}
                etiquette="Fin"
                enAttente={enAttente}
                onValider={(valeur) => {
                  onAgir(modifierPeriodeTarif(courante.id, { fin: valeur }));
                }}
              />
              {/*
                La suppression ne demande pas confirmation : le serveur REFUSE d'effacer une
                période qui porte des prix. Le seul cas qui passe est celle qu'on vient de
                créer de travers.
              */}
              <button
                type="button"
                className="lien"
                disabled={enAttente}
                onClick={() => {
                  onAgir(supprimerPeriodeTarif(courante.id));
                }}
              >
                Supprimer
              </button>
            </form>
          )}

          <form
            className="ajout"
            onSubmit={(e) => {
              e.preventDefault();
              if (!complet) return;
              const demande = { libelle: libelle.trim(), debut, fin };
              onAgir(
                depuis === ""
                  ? creerPeriodeTarif(demande)
                  : dupliquerPeriodeTarif(depuis, demande),
              );
            }}
          >
            <span className="periodes__titre">Nouvelle période</span>
            <input
              value={libelle}
              placeholder="Libellé"
              aria-label="Libellé de la nouvelle période"
              onChange={(e) => {
                setLibelle(e.target.value);
              }}
            />
            <label>
              du{" "}
              <input
                type="date"
                value={debut}
                aria-label="Début de la nouvelle période"
                onChange={(e) => {
                  setDebut(e.target.value);
                }}
              />
            </label>
            <label>
              au{" "}
              <input
                type="date"
                value={fin}
                aria-label="Fin de la nouvelle période"
                onChange={(e) => {
                  setFin(e.target.value);
                }}
              />
            </label>
            {grille.periodes.length > 0 && (
              <label>
                en reprenant les prix de{" "}
                <select
                  value={depuis}
                  onChange={(e) => {
                    setDepuis(e.target.value);
                  }}
                >
                  <option value="">aucune (grille vide)</option>
                  {grille.periodes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.libelle}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button type="submit" disabled={!complet || enAttente}>
              Créer
            </button>
          </form>

          <p className="note">
            Deux périodes ne peuvent pas se chevaucher : le prix d'un jour donné doit être
            décidable. Reprendre les prix d'une période donne un point de départ à corriger, pas
            une reconduction — c'est à vous de changer ceux qui ont bougé.
          </p>
        </div>
      )}
    </div>
  );
}

/** Une borne de période. Le calendrier du système, pas un calendrier dessiné à la main. */
function BorneDate({
  valeur,
  etiquette,
  enAttente,
  onValider,
}: {
  valeur: string;
  etiquette: string;
  enAttente: boolean;
  onValider: (valeur: string) => void;
}): JSX.Element {
  return (
    <label>
      {etiquette}{" "}
      <input
        type="date"
        value={valeur}
        disabled={enAttente}
        onChange={(e) => {
          if (e.target.value !== "" && e.target.value !== valeur) onValider(e.target.value);
        }}
      />
    </label>
  );
}

function AjoutArticle({
  onAjouter,
}: {
  onAjouter: (code: string, libelle: string) => void;
}): JSX.Element {
  const [code, setCode] = useState("");
  const [libelle, setLibelle] = useState("");
  const complet = code.trim() !== "" && libelle.trim() !== "";

  return (
    <form
      className="ajout"
      onSubmit={(e) => {
        e.preventDefault();
        if (!complet) return;
        onAjouter(code.trim(), libelle.trim());
        setCode("");
        setLibelle("");
      }}
    >
      <input
        placeholder="03-XX-XX-XXX"
        value={code}
        aria-label="Code du nouvel article"
        onChange={(e) => {
          setCode(e.target.value);
        }}
      />
      <input
        placeholder="Désignation"
        className="ajout__libelle"
        value={libelle}
        aria-label="Désignation du nouvel article"
        onChange={(e) => {
          setLibelle(e.target.value);
        }}
      />
      <button type="submit" disabled={!complet}>
        Ajouter
      </button>
    </form>
  );
}

/** Ce que le réimport a fait, dit en une phrase que personne n'a à interpréter. */
function Bilan({ bilan }: { bilan: BilanGrille }): JSX.Element {
  return (
    <div className="etat">
      <strong>
        {bilan.prixPoses} prix posé(s), {bilan.prixInchanges} inchangé(s),{" "}
        {bilan.articlesAjoutes} article(s) ajouté(s), {bilan.articlesModifies} modifié(s).
      </strong>{" "}
      {bilan.casesVides > 0 && (
        <>
          {bilan.casesVides} case(s) du classeur étaient vides : <strong>elles n'effacent
          rien</strong>. Pour retirer un prix, vider la case ici.
        </>
      )}
      {bilan.refusees.length > 0 && (
        <ul className="alerte">
          {bilan.refusees.map((r) => (
            <li key={`${String(r.ligne)}-${r.raison}`}>
              ligne {r.ligne} du classeur : {r.raison}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function GrilleTarifaire(): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [periodeVoulue, setPeriodeVoulue] = useState<string | null>(null);
  const [enAttente, setEnAttente] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [bilan, setBilan] = useState<BilanGrille | null>(null);
  const [montrerRetires, setMontrerRetires] = useState(false);
  const champFichier = useRef<HTMLInputElement>(null);

  const recharger = (id: string | null = periodeVoulue): void => {
    Promise.all([chargerGrille(id), chargerCarrieres()])
      .then(([grille, { carrieres }]) => {
        setEtat({ phase: "chargee", grille, carrieres });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(() => {
    recharger();
    // La période voulue est le seul déclencheur : le reste se recharge à la main après
    // chaque écriture, comme les autres référentiels.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodeVoulue]);

  const agir = (id: string | null, action: Promise<unknown>): void => {
    setEnAttente(id);
    setErreur(null);
    action
      .then(() => {
        recharger();
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(null);
      });
  };

  if (etat.phase === "chargement") return <p>Chargement de la grille tarifaire…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const { grille, carrieres } = etat;
  const periode = grille.periode;

  const colonnes = colonnesDeLaGrille(carrieres, grille.prix);

  const retires = grille.articles.filter((a) => !a.actif).length;
  const articles = montrerRetires ? grille.articles : grille.articles.filter((a) => a.actif);

  const parCroisement = new Map(grille.prix.map((p) => [`${p.articleId}|${p.carriereId}`, p.prix]));
  const croisements = articles.length * colonnes.length;

  const fixer = (article: ArticleTarif, carriere: Carriere, saisi: string): void => {
    if (periode === null) return;
    agir(
      `${article.id}|${carriere.id}`,
      fixerPrixTarif({
        periodeId: periode.id,
        articleId: article.id,
        carriereId: carriere.id,
        prix: saisi === "" ? null : saisi.replace(",", "."),
      }),
    );
  };

  return (
    <section className="referentiel">
      <h2>
        Grille tarifaire des carrières — {grille.articles.length - retires} article(s)
        {retires > 0 && (
          <>
            {" · "}
            <button
              type="button"
              className="lien"
              onClick={() => {
                setMontrerRetires(!montrerRetires);
              }}
            >
              {montrerRetires ? "masquer" : "voir"} les {retires} retiré(s)
            </button>
          </>
        )}
      </h2>

      <p className="note">
        Le prix qu'une ligne de contre-étude reçoit quand le conducteur coche « du groupe » et
        désigne la carrière. La période retenue est celle qui couvre la <strong>date de
        démarrage du chantier</strong> — c'est le tarif qui sera facturé quand les camions
        rouleront. Corriger un prix ici ne retouche aucun chiffrage déjà fait : le prix est
        recopié sur la ligne au moment du choix.
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      <Periodes
        grille={grille}
        enAttente={enAttente !== null}
        onChoisir={setPeriodeVoulue}
        onAgir={(action) => {
          agir(null, action);
        }}
      />

      {periode === null ? (
        <p className="etat">
          Aucune période de validité : en créer une pour commencer à saisir des prix. Les
          tarifs s'historisent par période, il en faut donc au moins une.
        </p>
      ) : (
        <>
          <AjoutArticle
            onAjouter={(code, libelle) => {
              agir(null, creerArticleTarif({ code, libelle }));
            }}
          />

          <p className="etat">
            <a href={adresseClasseurGrille(periode.id)} download>
              Exporter le classeur
            </a>
            {" · "}
            <button type="button" className="lien" onClick={() => champFichier.current?.click()}>
              Réimporter un classeur dans « {periode.libelle} »
            </button>
            <input
              ref={champFichier}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => {
                const fichier = e.target.files?.[0];
                e.target.value = "";
                if (fichier === undefined) return;
                setBilan(null);
                setErreur(null);
                importerGrille(periode.id, fichier)
                  .then((resultat) => {
                    setBilan(resultat);
                    recharger();
                  })
                  .catch((err: unknown) => {
                    setErreur(err instanceof Error ? err.message : String(err));
                  });
              }}
            />
            {" · "}
            <span className="note">
              {grille.prix.length} prix renseigné(s) sur {croisements} croisement(s) — une case
              vide n'est pas une erreur, c'est un prix qu'on ne connaît pas.
            </span>
          </p>

          {bilan !== null && <Bilan bilan={bilan} />}

          {colonnes.length === 0 ? (
            <p className="alerte">
              Aucune carrière au référentiel : la grille n'a aucune colonne. En ajouter
              ci-dessus.
            </p>
          ) : (
            <div className="grille-tarifaire">
              <table className="ressources">
                {/*
                  La grille des colonnes (Chris, 01/09/2026 : « les libellés sont tronqués »).
                  Sans elle, `table-layout: fixed` sur `width: 100%` partageait la fenêtre à
                  parts égales entre le code, la désignation, les six carrières et les actions —
                  la désignation d'un matériau recevait la largeur d'un prix.

                  **Ces deux largeurs-ci ne sont pas mesurées** : la grille vit en base de
                  production, à laquelle l'accès m'a été refusé. 32 caractères est le calibre des
                  libellés du référentiel générique, mesuré lui ; l'infobulle rattrape ce qui
                  dépasse. À resserrer ou élargir une fois vue sur les vraies désignations.

                  Les colonnes de prix suivent leur champ de saisie (`.col-prix .saisie`, 6rem) :
                  ce qui les dimensionne est un nombre à quatre décimales, pas l'en-tête, qui a
                  le droit de passer à la ligne.
                */}
                <colgroup>
                  <col style={{ width: "calc(14ch + 0.7rem)" }} />
                  <col style={{ width: "calc(32ch + 0.7rem)" }} />
                  {colonnes.map((c) => (
                    <col key={c.id} style={{ width: "7.5rem" }} />
                  ))}
                  <col style={{ width: "9.5rem" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Désignation</th>
                    {colonnes.map((c) => (
                      <th key={c.id} className={c.actif ? undefined : "retiree"}>
                        {c.denomination}
                      </th>
                    ))}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {articles.map((a) => (
                    <tr key={a.id} className={a.actif ? undefined : "retiree"}>
                      <td className="code" title={a.code}>
                        {a.code}
                      </td>
                      <td>
                        <Champ
                          valeur={a.libelle}
                          infobulle={a.libelle}
                          enAttente={enAttente === a.id}
                          ariaLabel={`Désignation de ${a.code}`}
                          onValider={(valeur) => {
                            agir(a.id, modifierArticleTarif(a.id, { libelle: valeur }));
                          }}
                        />
                      </td>
                      {colonnes.map((c) => {
                        const cle = `${a.id}|${c.id}`;
                        const prix = parCroisement.get(cle);
                        return (
                          <td key={c.id} className="col-prix">
                            <Champ
                              valeur={prix === undefined ? "" : prixLisible(prix)}
                              placeholder="—"
                              aligne="droite"
                              enAttente={enAttente === cle}
                              ariaLabel={`Prix de ${a.code} à ${c.denomination}`}
                              onValider={(valeur) => {
                                fixer(a, c, valeur);
                              }}
                            />
                          </td>
                        );
                      })}
                      <td>
                        <button
                          type="button"
                          className="lien"
                          disabled={enAttente === a.id}
                          onClick={() => {
                            agir(a.id, modifierArticleTarif(a.id, { actif: !a.actif }));
                          }}
                        >
                          {a.actif ? "Retirer" : "Remettre"}
                        </button>
                        {" · "}
                        <button
                          type="button"
                          className="lien"
                          disabled={enAttente === a.id}
                          onClick={() => {
                            agir(a.id, supprimerArticleTarif(a.id));
                          }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                  {articles.length === 0 && (
                    <tr>
                      <td colSpan={colonnes.length + 3} className="vide">
                        Aucun article. En ajouter un, ou réimporter le classeur des tarifs.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
